import { Component, OnInit } from "@angular/core";
import { Router } from "@angular/router";
import { BehaviorSubject, of, forkJoin } from "rxjs";
import { tap, finalize } from "rxjs/operators";
import { GridOptions, ColDef, GridApi } from "ag-grid-community";


import { ActionCellRendererComponent } from "../../ag-grid/action-cell-renderer.component";
import { SubHeader } from "../../../../core/models/sub-header.model";
import { OrganizationService } from "../../../services/organization.service";
import { SubHeaderService } from "../../../../core/services/sub-header.service";
import { Subscription, Observable } from "rxjs";
import { OrganizationComplianceDetails, OrganizationEventType, Organization, FinancialComplianceSearchSpec, MassMailingEntry } from "../../../models/organization.model";
import { GridFormatterService } from "../../../../common/services/grid-formatter.service";
import { DatePipe } from "@angular/common";
import { dateComparator } from '../../../../common/components/data-table/comparators/data-table.comparators';
import { DynamicDialogService } from "../../../../core/services/dynamic-dialog.service";
import { Dialog, DialogButton } from "../../../../core/models/dialog.model";

@Component({
    selector: 'org-mass-mailing',
    templateUrl: './organization-mass-mailing.component.html',
    standalone: false
})
export class OrganizationMassMailingComponent implements OnInit {


    complianceDetails: OrganizationComplianceDetails[] = [];

    dataLoaded: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
    dataNotReadyDialog: Dialog = new Dialog("Please wait for data to finish loading.", [new DialogButton("OK", false, () => { return; })]);

    columnActive = {
        headerName: 'Active',
        headerTooltip: 'Active',
        field: 'active',
        valueGetter: this.gridFormatterService.formatBooleanToYesNo("active"),
        tooltipValueGetter: this.gridFormatterService.formatBooleanToYesNo("active"),
        maxWidth: 100
    } as ColDef;

    columnLegacyOwnerNumber = {
        headerName: 'Legacy Owner Number',
        headerTooltip: 'Legacy Owner Number',
        field: 'rbdmsOwnerNumber',
        tooltipField: "rbdmsOwnerNumber",
        filter: "agTextColumnFilter",
        filterParams: this.gridFormatterService.defaultTextFilterParams,
        maxWidth: 170
    } as ColDef;

    columnName = {
        headerName: 'Organization Name',
        headerTooltip: 'Organization Name',
        field: 'organizationName',
        tooltipField: "organizationName",
        filter: "agTextColumnFilter",
        filterParams: this.gridFormatterService.defaultTextFilterParams,
        cellRenderer: ActionCellRendererComponent,
        cellRendererParams: {
            textField: 'organizationName',
            action: this.showDetail()
        }
    } as ColDef;

    columnOwnerNumber = {
        headerName: 'Owner Number',
        headerTooltip: 'Owner Number',
        field: 'ricsOwnerNumber',
        tooltipField: "ricsOwnerNumber",
        filter: "agTextColumnFilter",
        filterParams: this.gridFormatterService.defaultTextFilterParams,
        maxWidth: 150
    } as ColDef;

    columnStatus = {
        headerName: 'Compliance Status',
        headerTooltip: 'Compliance Status',
        field: 'inCompliance',
        tooltipValueGetter: this.complianceStatusText(this),
        valueGetter: this.complianceStatusText(this),
        maxWidth: 200
    } as ColDef;

    columnType = {
        headerName: 'Business Entity Type',
        headerTooltip: 'Business Entity Type',
        field: 'entityType',
        valueGetter: this.typeFormatter(this),
        tooltipValueGetter: this.typeFormatter(this),
        minWidth: 200,
        maxWidth: 300
    } as ColDef;

    columnRicsRegistered = {
        headerName: 'RICS Registered?',
        headerTooltip: 'RICS Registered?',
        field: 'registeredInRICS',
        valueGetter: this.gridFormatterService.formatBooleanToYesNo("registeredInRICS"),
        tooltipValueGetter: this.gridFormatterService.formatBooleanToYesNo("registeredInRICS"),
        minWidth: 100
    } as ColDef;

    columnContactName = {
        headerName: 'Primary Contact Name',
        headerTooltip: 'Primary Contact Name',
        valueGetter: this.gridFormatterService.formatPotentiallyBlankField("primaryContactName", "N/A"),
        tooltipValueGetter: this.gridFormatterService.formatPotentiallyBlankField("primaryContactName", "N/A"),
        filter: "agTextColumnFilter",
        filterParams: this.gridFormatterService.defaultTextFilterParams,
        minWidth: 100
    } as ColDef;

    columnContactPhone = {
        headerName: 'Primary Contact Phone Number',
        headerTooltip: 'Primary Contact Phone Number',
        valueGetter: this.gridFormatterService.formatPotentiallyBlankField("primaryContactPhone", "N/A"),
        tooltipValueGetter: this.gridFormatterService.formatPotentiallyBlankField("primaryContactPhone", "N/A"),
        filter: "agTextColumnFilter",
        filterParams: this.gridFormatterService.defaultTextFilterParams,
        minWidth: 100
    } as ColDef;

    columnContactEmail = {
        headerName: 'Primary Contact Email Address',
        headerTooltip: 'Primary Contact Email Address',
        valueGetter: this.gridFormatterService.formatPotentiallyBlankField("primaryContactEmail", "N/A"),
        tooltipValueGetter: this.gridFormatterService.formatPotentiallyBlankField("primaryContactEmail", "N/A"),
        filter: "agTextColumnFilter",
        filterParams: this.gridFormatterService.defaultTextFilterParams,
        minWidth: 150
    } as ColDef;

    columnOrganizationStreet = {
        headerName: 'Organization Address',
        headerTooltip: 'Organization Address',
        field: "organizationStreetAddress",
        tooltipField: "organizationStreetAddress",
        filter: "agTextColumnFilter",
        filterParams: this.gridFormatterService.defaultTextFilterParams,
        minWidth: 250
    } as ColDef;

    columnOrganizationSuite = {
        headerName: 'Organization Suite',
        headerTooltip: 'Organization Suite',
        field: "organizationSuite",
        tooltipField: "organizationSuite",
        filter: "agTextColumnFilter",
        filterParams: this.gridFormatterService.defaultTextFilterParams,
        minWidth: 150
    } as ColDef;

    columnOrganizationPoBox = {
        headerName: 'Organization P.O. Box',
        headerTooltip: 'Organization P.O. Box',
        field: "organizationPOBox",
        tooltipField: "organizationPOBox",
        filter: "agTextColumnFilter",
        filterParams: this.gridFormatterService.defaultTextFilterParams,
        minWidth: 150
    } as ColDef;

    columnOrganizationCity = {
        headerName: 'Organization City',
        headerTooltip: 'Organization City',
        field: "organizationCity",
        tooltipField: "organizationCity",
        filter: "agTextColumnFilter",
        filterParams: this.gridFormatterService.defaultTextFilterParams,
        minWidth: 150
    } as ColDef;

    columnOrganizationState = {
        headerName: 'Organization State',
        headerTooltip: 'Organization State',
        field: "organizationState",
        tooltipField: "organizationState",
        filter: "agTextColumnFilter",
        filterParams: this.gridFormatterService.defaultTextFilterParams,
        minWidth: 150
    } as ColDef;

    columnOrganizationZip = {
        headerName: 'Organization Zip',
        headerTooltip: 'Organization Zip',
        field: "organizationZipCode",
        tooltipField: "organizationZipCode",
        filter: "agTextColumnFilter",
        filterParams: this.gridFormatterService.defaultTextFilterParams,
        minWidth: 150
    } as ColDef;

    dateRegisteredColumn = {
        headerName: 'Date Registered',
        headerTooltip: 'Date Registered',
        field: "registrationDate",
        valueFormatter: this.gridFormatterService.formatDate('registrationDate', "MM/dd/yyyy", "N/A"),
        tooltipValueGetter: this.gridFormatterService.formatDate('registrationDate', "MM/dd/yyyy", "N/A"),
        filter: "agDateColumnFilter",
        filterParams: this.gridFormatterService.defaultDateFilterParams,
        comparator: dateComparator,
        minWidth: 150
    } as ColDef;

    dateCancelledColumn = {
        headerName: "Date Cancellation Approved",
        headerTooltip: "Date Cancellation Approved",
        field: "cancellationDate",
        tooltipValueGetter: this.gridFormatterService.formatDate("cancellationDate"),
        valueFormatter: this.gridFormatterService.formatDate("cancellationDate"),
        filter: "agDateColumnFilter",
        filterParams: this.gridFormatterService.defaultDateFilterParams,
        comparator: dateComparator,
        minWidth: 200
    } as ColDef;

    data: Organization[];

    data$: Observable<MassMailingEntry[]> = of(null);

    gridColumns = [
        this.columnName,
        this.columnOwnerNumber,
        this.columnLegacyOwnerNumber,
        this.columnRicsRegistered,
        this.columnStatus,
        this.columnActive,
        this.columnType,
        this.columnContactName,
        this.columnContactPhone,
        this.columnContactEmail,
        this.columnOrganizationStreet,
        this.columnOrganizationSuite,
        this.columnOrganizationPoBox,
        this.columnOrganizationCity,
        this.columnOrganizationState,
        this.columnOrganizationZip,
        this.dateRegisteredColumn,
        this.dateCancelledColumn
    ];

	gridOptions: GridOptions;
	gridApi?: GridApi;
    subscriptions: Subscription = new Subscription();


    subHeader: SubHeader = new SubHeader({ title: "Mass Mailing", buttons: [{ name: "Download Report", method: () => this.downloadReport() }] });

    constructor(public service: OrganizationService,
        public subHeaderService: SubHeaderService,
        public router: Router,
        public gridFormatterService: GridFormatterService,
        private dialogService: DynamicDialogService,
		private datePipe: DatePipe) {
        this.gridFormatterService.applyFilterKeyboardHintTooltips(this.gridColumns);
    }

    ngOnInit() {

        this.gridOptions = <GridOptions>{
			columnDefs: this.gridColumns,
			defaultColDef: { filter: true, sortable: true, headerClass: "wrapped" } as ColDef,
			enableBrowserTooltips: false,
            tooltipTrigger: 'focus',
            headerHeight: 50,
			getRowClass: (params: any) => {
				if (!this.complianceStatus(this, params.data)) return 'red';
			},
            onGridReady: (params) => {
				this.gridApi = params.api;
            },
            pagination: true,
            paginationAutoPageSize: true,
        };

        this.subHeaderService.updateSubHeader(this.subHeader);

        this.data$ = this.service.getMassMailingList().pipe(
            tap((items: MassMailingEntry[]) => {
                this.dataLoaded.next(true);
            })
        );
    }

    ngOnDestroy() {
        this.subscriptions.unsubscribe();
    }

    getTypeDescription(code: string): string {
        return this.service.getTypeDescription(code);
    }

    showDetail() {
        return (obj: any) => {
            const id = obj.data.ricsOwnerNumber;
            this.router.navigate(['/common/organization', id]);
        }
    }

    contactFormatter() {
        return (obj: any) => {
            const contact = obj.data.contacts[0];
            switch (obj.colDef.headerName) {
                case "Primary Contact Name":
                    return contact ? contact.displayName : '';
                case "Primary Contact Phone Number":
                    return contact ? contact.primaryPhone : '';
                case "Primary Contact Email Address":
                    return contact ? contact.emailAddress : '';
            }
        }
    }

    eventDateFormatter(field: string) {
        return (obj: any) => this.getDateOrNotApplicable(obj.data, field);
    }

    getDateOrNotApplicable(data: any, field: string) : string {
        if (data.history.length > 0) {
            let evt = null;
            if (field == 'cancellation') {
                evt = data.history.find(x => x.eventType === OrganizationEventType.OrganizationCancelled);
            }
            if (field == 'registration') {
                evt = data.history.find(x => x.eventType === OrganizationEventType.OrganizationCreated);
            }
            return (!evt) ? 'N/A' : this.datePipe.transform(evt.timestamp, "MM/dd/yyyy");
        }
        return 'N/A';
    }

    typeFormatter(component: any) {
        return (obj) => component.getTypeDescription(obj.data.entityType)
    }

    complianceStatus(component, obj): boolean {
        let complianceStatus: boolean = false;
        if (obj && obj["inCompliance"] != null) {
            complianceStatus = <boolean>obj["inCompliance"];
        }
        return complianceStatus;
    }

    complianceStatusText(component: any) {
        return (obj) => {
            let response = "Not on File";
            if (obj && obj.data && obj.data["inCompliance"] != null) {
                response = (<boolean>obj.data["inCompliance"]) ? 'Compliant' : 'Out of Compliance';
            }
            return response;
        }
    }

    statusFormatter(component: any) {
        return (obj: any) => component.getComplianceStatusDescription(obj.data.complianceStatus);
    }

    downloadReport() {
        if (this.dataLoaded.value == true) {
            const today = this.datePipe.transform(new Date(), "yyyyMMdd")
            const params = {
                skipHeader: false,
                allColumns: true,
                fileName: `Mailing List_${today}`,
                sheetName: "Mailing List",
                exportMode: "xlsx"
            }
            this.gridFormatterService.exportDataToExcel(params.fileName, params.sheetName, this.gridApi);
        } else {
            this.dataNotReadyDialog.toggleDialog(true);
            this.dialogService.updateDialog(this.dataNotReadyDialog);
        }
    
    }

    onGridPageChanged(event: any) {
        if (this.gridApi) {
            this.gridApi.autoSizeAllColumns();
        }
    }
}
